import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'
import type {SortDir} from '@/hooks/common/use-table-state'

export type Proveedor = components['schemas']['ProveedorRead']
export type ProveedorCreate = components['schemas']['ProveedorCreate']
export type ProveedorUpdate = components['schemas']['ProveedorUpdate']
export type ProveedorEvaluacion = components['schemas']['ProveedorEvaluacionRead']
export type ProveedorEvaluacionCreate = components['schemas']['ProveedorEvaluacionCreate']

export const PROVEEDORES_PAGE_SIZE = 10
const EVALUACIONES_PAGE_SIZE = 10

export type ProveedorListParams = {
  page: number
  search: string
  sortBy: string
  sortDir: SortDir
}

export function useProveedores(businessId: string | undefined, params: ProveedorListParams) {
  return useQuery({
    queryKey: ['proveedores', businessId, params],
    queryFn: async () =>
      (
        await api.get<Page<Proveedor>>(`/businesses/${businessId}/proveedores`, {
          params: {
            page: params.page,
            page_size: PROVEEDORES_PAGE_SIZE,
            search: params.search || undefined,
            sort_by: params.sortBy,
            sort_dir: params.sortDir,
          },
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

function invalidateProveedores(queryClient: ReturnType<typeof useQueryClient>, businessId: string) {
  queryClient.invalidateQueries({queryKey: ['proveedores', businessId]})
}

export function useCreateProveedor(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProveedorCreate) =>
      (await api.post<Proveedor>(`/businesses/${businessId}/proveedores`, input)).data,
    onSuccess: () => invalidateProveedores(queryClient, businessId),
  })
}

export function useUpdateProveedor(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({id, input}: {id: string; input: ProveedorUpdate}) =>
      (await api.patch<Proveedor>(`/businesses/${businessId}/proveedores/${id}`, input)).data,
    onSuccess: () => invalidateProveedores(queryClient, businessId),
  })
}

export function useDeleteProveedor(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/businesses/${businessId}/proveedores/${id}`)
    },
    onSuccess: () => invalidateProveedores(queryClient, businessId),
  })
}

/** El endpoint es `PaginaSola`: el orden lo fija el servidor y no hay búsqueda. */
export function useProveedorEvaluaciones(
  businessId: string | undefined,
  proveedorId: string | undefined,
  page: number
) {
  return usePagedList<ProveedorEvaluacion>(
    ['proveedores', businessId, proveedorId, 'evaluaciones'],
    businessId && proveedorId ? `/businesses/${businessId}/proveedores/${proveedorId}/evaluaciones` : undefined,
    {page, search: ''},
    EVALUACIONES_PAGE_SIZE
  )
}

export function useCreateProveedorEvaluacion(businessId: string, proveedorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProveedorEvaluacionCreate) =>
      (await api.post<ProveedorEvaluacion>(`/businesses/${businessId}/proveedores/${proveedorId}/evaluaciones`, input))
        .data,
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: ['proveedores', businessId, proveedorId, 'evaluaciones']}),
  })
}

export function useAttachEvaluacionEvidencia(businessId: string, proveedorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      evaluacionId,
      payload,
    }: {
      evaluacionId: string
      payload: {object_key: string; filename: string; content_type: string; size_bytes: number}
    }) =>
      (
        await api.post<ProveedorEvaluacion>(
          `/businesses/${businessId}/proveedores/${proveedorId}/evaluaciones/${evaluacionId}/evidencia`,
          payload
        )
      ).data,
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: ['proveedores', businessId, proveedorId, 'evaluaciones']}),
  })
}
